/**
 * Resumable Upload Utility
 * Implements chunked file upload with resume capability
 * Stores upload state in localStorage for persistence across page reloads
 */

const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
const STORAGE_KEY_PREFIX = 'pv_upload_';

/**
 * Generate unique file identifier based on file properties
 */
const generateFileId = (file) => {
  return `${file.name}_${file.size}_${file.lastModified}`;
};

/**
 * Get upload state from localStorage
 */
const getUploadState = (fileId) => {
  try {
    const state = localStorage.getItem(`${STORAGE_KEY_PREFIX}${fileId}`);
    return state ? JSON.parse(state) : null;
  } catch (err) {
    console.error('Error reading upload state:', err);
    return null;
  }
};

/**
 * Save upload state to localStorage
 */
const saveUploadState = (fileId, state) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${fileId}`, JSON.stringify(state));
  } catch (err) {
    console.error('Error saving upload state:', err);
  }
};

/**
 * Clear upload state from localStorage
 */
const clearUploadState = (fileId) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${fileId}`);
  } catch (err) {
    console.error('Error clearing upload state:', err);
  }
};

/**
 * Upload a single chunk
 */
const uploadChunk = (url, file, chunkIndex, chunkSize, formData, onProgress) => {
  return new Promise((resolve, reject) => {
    const start = chunkIndex * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    const xhr = new XMLHttpRequest();
    const chunkFormData = new FormData();

    // Add chunk metadata
    chunkFormData.append('chunk', chunk);
    chunkFormData.append('chunkIndex', chunkIndex);
    chunkFormData.append('totalChunks', Math.ceil(file.size / chunkSize));
    chunkFormData.append('fileName', file.name);
    chunkFormData.append('fileSize', file.size);
    chunkFormData.append('fileId', generateFileId(file));

    // Add form data
    if (formData) {
      chunkFormData.append('formData', formData);
    }

    // Progress tracking for this chunk
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const chunkProgress = (e.loaded / e.total) * 100;
        const overallProgress = ((chunkIndex * chunkSize + e.loaded) / file.size) * 100;
        onProgress(Math.round(overallProgress), chunkIndex);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve(result);
        } catch (err) {
          reject(new Error('Invalid server response'));
        }
      } else {
        reject(new Error(`Chunk upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during chunk upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Chunk upload cancelled'));
    });

    xhr.open('POST', url, true);
    xhr.send(chunkFormData);
  });
};

/**
 * Resumable upload with chunking
 */
export const resumableUpload = async (
  url,
  file,
  formData,
  options = {}
) => {
  const {
    chunkSize = CHUNK_SIZE,
    maxRetries = 3,
    onProgress = () => {},
    onChunkComplete = () => {},
    onResume = () => {},
  } = options;

  const fileId = generateFileId(file);
  const totalChunks = Math.ceil(file.size / chunkSize);

  // Check for existing upload state
  let uploadState = getUploadState(fileId);
  let startChunk = 0;

  if (uploadState && uploadState.totalChunks === totalChunks) {
    startChunk = uploadState.completedChunks;
    if (startChunk > 0) {
      console.log(`Resuming upload from chunk ${startChunk}/${totalChunks}`);
      onResume(startChunk, totalChunks);
    }
  } else {
    // New upload
    uploadState = {
      fileId,
      fileName: file.name,
      fileSize: file.size,
      totalChunks,
      completedChunks: 0,
      startTime: Date.now(),
    };
    saveUploadState(fileId, uploadState);
  }

  // Upload chunks sequentially
  for (let chunkIndex = startChunk; chunkIndex < totalChunks; chunkIndex++) {
    let retryCount = 0;
    let chunkUploaded = false;

    while (!chunkUploaded && retryCount <= maxRetries) {
      try {
        await uploadChunk(
          url,
          file,
          chunkIndex,
          chunkSize,
          formData,
          onProgress
        );

        chunkUploaded = true;
        uploadState.completedChunks = chunkIndex + 1;
        saveUploadState(fileId, uploadState);
        onChunkComplete(chunkIndex + 1, totalChunks);

      } catch (err) {
        retryCount++;
        console.error(`Chunk ${chunkIndex} failed (attempt ${retryCount}/${maxRetries}):`, err);

        if (retryCount > maxRetries) {
          throw new Error(`Failed to upload chunk ${chunkIndex} after ${maxRetries} retries: ${err.message}`);
        }

        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount - 1) * 1000));
      }
    }
  }

  // Upload complete, clear state
  clearUploadState(fileId);

  return {
    success: true,
    fileId,
    fileName: file.name,
    fileSize: file.size,
    totalChunks,
  };
};

/**
 * Check if file has pending upload
 */
export const hasPendingUpload = (file) => {
  const fileId = generateFileId(file);
  const state = getUploadState(fileId);
  return state && state.completedChunks < state.totalChunks;
};

/**
 * Get pending upload progress
 */
export const getPendingUploadProgress = (file) => {
  const fileId = generateFileId(file);
  const state = getUploadState(fileId);
  if (!state) return null;

  return {
    completedChunks: state.completedChunks,
    totalChunks: state.totalChunks,
    progress: Math.round((state.completedChunks / state.totalChunks) * 100),
  };
};

/**
 * Clear all pending uploads
 */
export const clearAllPendingUploads = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (err) {
    console.error('Error clearing pending uploads:', err);
  }
};

/**
 * Get all pending uploads
 */
export const getAllPendingUploads = () => {
  try {
    const keys = Object.keys(localStorage);
    const pendingUploads = [];

    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        const state = JSON.parse(localStorage.getItem(key));
        if (state.completedChunks < state.totalChunks) {
          pendingUploads.push({
            fileId: state.fileId,
            fileName: state.fileName,
            fileSize: state.fileSize,
            progress: Math.round((state.completedChunks / state.totalChunks) * 100),
            completedChunks: state.completedChunks,
            totalChunks: state.totalChunks,
            startTime: state.startTime,
          });
        }
      }
    });

    return pendingUploads;
  } catch (err) {
    console.error('Error getting pending uploads:', err);
    return [];
  }
};
